export interface CmmInputUniqueValueItem {
  count: number;
  value: string | number | boolean;
}

/** Column from Firestore. top_values may be items with { value, count } or plain values (string/number/boolean). */
export interface CmmInputUniqueValuesColumn {
  column_name: string;
  field_name?: string;
  display_name?: string;
  show_on_condition_list: boolean;
  data_type: string;
  top_values: (CmmInputUniqueValueItem | string | number | boolean)[];
}

export interface CmmInputUniqueValuesDoc {
  id: string;
  created_at_range_end?: string;
  created_at_range_start?: string;
  columns: CmmInputUniqueValuesColumn[];
  updated_at?: string;
}

export type TopValueEntry =
  | CmmInputUniqueValueItem
  | string
  | number
  | boolean;

/**
 * Coerces Firestore `top_values` into an array. Values may be stored as a map when
 * Firestore rewrites sparse arrays or legacy documents use object shapes.
 *
 * @param topValues - Raw `top_values` from Firestore
 * @returns Array safe for `.slice()` and `.map()`
 */
export function normalizeTopValues(topValues: unknown): TopValueEntry[] {
  if (topValues == null) return [];
  if (Array.isArray(topValues)) return topValues as TopValueEntry[];
  if (typeof topValues === "object") {
    return Object.values(topValues) as TopValueEntry[];
  }
  return [topValues as TopValueEntry];
}

/**
 * Coerces Firestore `columns` into an array when stored as a map/object.
 *
 * @param columns - Raw `columns` from Firestore
 * @returns Array of column definitions
 */
export function normalizeColumns(columns: unknown): CmmInputUniqueValuesColumn[] {
  if (columns == null) return [];
  if (Array.isArray(columns)) return columns as CmmInputUniqueValuesColumn[];
  if (typeof columns === "object") {
    return Object.values(columns) as CmmInputUniqueValuesColumn[];
  }
  return [];
}

/**
 * Normalizes a CMM input unique values document so `columns` and each `top_values` are arrays.
 *
 * @param doc - Raw document from Firestore
 * @returns Document with array-shaped `columns` and `top_values`, or null
 */
export function normalizeCmmInputUniqueValuesDoc(
  doc: CmmInputUniqueValuesDoc | null,
): CmmInputUniqueValuesDoc | null {
  if (!doc) return null;
  const columns = normalizeColumns(doc.columns).map((col) => ({
    ...col,
    top_values: normalizeTopValues(col.top_values),
  }));
  return { ...doc, columns };
}
